/**
 * Column Mappings Management Organism
 *
 * Displays current column mappings for a deal (TB or GL) with:
 * - Table view: read-only display of current mappings
 * - Edit mode: change mapped columns via dropdowns (using stored available columns from original file)
 * - Upload flow: upload new Excel file → pick header row → map fields via dropdowns
 */

import { useState, useCallback } from "react";
import { Upload, FileSpreadsheet, X, Save, Edit2, AlertCircle } from "lucide-react";
import { Card, Button, Badge } from "@/ui/atoms";
import { EmptyState } from "@/ui/molecules";
import { cn } from "@/utils/utils";
import type {
    TApiColumnMappingFieldType,
    TApiColumnMappingFileType,
    TApiGlType,
} from "@/apis/proda_finance";
import {
    TB_COLUMN_FIELDS,
    GL_COLUMN_FIELDS,
    type TColumnMappingFieldConfig,
} from "./columnFieldConfigs";
import {
    FormulaModal,
    buildFormulaExpression,
    formatColumnDisplay,
    parseFormulaExpression,
} from "@/ui/molecules/AmountHeaderFormulaModal";

// ─── Types ────────────────────────────────────────────────────────────────────

export type TColumnMappingView = {
    id: string;
    fileType: TApiColumnMappingFileType;
    fieldType: TApiColumnMappingFieldType;
    columnName: string;
    headerRowNumber: number;
    isActive: boolean;
    availableColumns: string[] | null;
};

export type TColumnMappingActions = {
    fetchMappings: (dealId: string, fileType: TApiColumnMappingFileType) => Promise<void>;
    updateMappings: (
        dealId: string,
        fileType: TApiColumnMappingFileType,
        headerRowNumber: number,
        mappings: Array<{ field_type: TApiColumnMappingFieldType; column_name: string }>,
        availableColumns?: string[],
    ) => Promise<void>;
    extractColumns: (
        file: File,
        headerRowNumber: number,
    ) => Promise<{
        success: boolean;
        columns: string[];
        errors: string[];
        warnings: string[];
    }>;
};

type TDealSegmentInfo = {
    requiresSegment2: boolean;
    requiresSegment4: boolean;
    requiresSegment5: boolean;
    requiresSegment6: boolean;
    requiresSegment7: boolean;
    segment2MappingType: string;
    segment4MappingType: string;
    segment5MappingType: string;
    segment6MappingType: string;
    segment7MappingType: string;
    /** Deal file type ("tb_only" | "tb_and_gl"). Drives whether TB segment columns are optional. */
    dealFileType?: string;
    /**
     * CoA mapping mode of the deal's business partner. "multi_criteria" offers
     * the second source-account column on the GL. Absent reads as single-key,
     * so deals loaded before this field existed behave as they always did.
     */
    coaMappingMode?: string;
};

type TProps = {
    dealId: string;
    fileType: TApiColumnMappingFileType;
    mappings: TColumnMappingView[] | null;
    loading: boolean;
    deal: TDealSegmentInfo;
    actions: TColumnMappingActions;
    canEdit?: boolean;
    /**
     * GL type for the deal. Drives whether the GL Period column is mandatory:
     * required for YTD, optional for MTD. Ignored for TB mappings.
     */
    glType?: TApiGlType | null;
};

type TMode = "view" | "edit" | "upload";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getApplicableFields(
    fileType: TApiColumnMappingFileType,
    deal: TDealSegmentInfo,
    glType?: TApiGlType | null,
): TColumnMappingFieldConfig[] {
    const fields = fileType === "tb" ? TB_COLUMN_FIELDS : GL_COLUMN_FIELDS;
    // Keyed lookups rather than ternary chains: an unlisted segment must resolve to
    // undefined (field hidden), never silently fall through to another segment's value.
    const requiresBySegment: Record<number, boolean | undefined> = {
        2: deal.requiresSegment2,
        4: deal.requiresSegment4,
        5: deal.requiresSegment5,
        6: deal.requiresSegment6,
        7: deal.requiresSegment7,
    };
    const mappingTypeBySegment: Record<number, string | undefined> = {
        2: deal.segment2MappingType,
        4: deal.segment4MappingType,
        5: deal.segment5MappingType,
        6: deal.segment6MappingType,
        7: deal.segment7MappingType,
    };
    return fields
        .filter((f) => {
            // Multi-criteria columns are absent unless the partner uses them.
            // Checked before the segment filter because the two are unrelated:
            // this field has no conditionalSegment and would otherwise always
            // pass.
            if (f.multiCriteriaOnly && deal.coaMappingMode !== "multi_criteria") return false;
            if (!f.conditionalSegment) return true;
            const seg = f.conditionalSegment;
            if (!requiresBySegment[seg]) return false;
            const mappingType = mappingTypeBySegment[seg];
            return mappingType !== undefined && mappingType !== "no_mapping";
        })
        .map((f) => {
            // The GL Period column is only mandatory for YTD deals. For MTD (or when
            // the GL type is unknown) it is optional.
            if (fileType === "gl" && f.fieldType === "period") {
                return { ...f, required: glType === "ytd" };
            }
            // TBGL deals: the segment source lives in the GL file, so TB segment columns
            // are optional (still shown/mappable). GL segment columns stay required.
            if (fileType === "tb" && deal.dealFileType === "tb_and_gl" && f.conditionalSegment) {
                return { ...f, required: false };
            }
            return f;
        });
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const OrganismColumnMappings = ({
    dealId,
    fileType,
    mappings,
    loading,
    deal,
    actions,
    canEdit = true,
    glType,
}: TProps) => {
    const [mode, setMode] = useState<TMode>("view");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Edit mode state
    const [editMappings, setEditMappings] = useState<Record<TApiColumnMappingFieldType, string>>(
        {} as Record<TApiColumnMappingFieldType, string>,
    );
    const [editFormulaOpen, setEditFormulaOpen] = useState(false);

    // Upload flow state
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadHeaderRow, setUploadHeaderRow] = useState<number>(1);
    const [extractedColumns, setExtractedColumns] = useState<string[]>([]);
    const [uploadMappings, setUploadMappings] = useState<
        Record<TApiColumnMappingFieldType, string>
    >({} as Record<TApiColumnMappingFieldType, string>);
    const [uploadFormulaOpen, setUploadFormulaOpen] = useState(false);
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractError, setExtractError] = useState<string | null>(null);

    const applicableFields = getApplicableFields(fileType, deal, glType);

    const storedAvailableColumns = mappings?.[0]?.availableColumns ?? null;
    const currentHeaderRow = mappings?.[0]?.headerRowNumber ?? 1;

    const startEdit = useCallback(() => {
        const current: Record<TApiColumnMappingFieldType, string> = {} as Record<
            TApiColumnMappingFieldType,
            string
        >;
        for (const m of mappings ?? []) {
            current[m.fieldType] = m.columnName;
        }
        setEditMappings(current);
        setMode("edit");
        setError(null);
        setSuccessMessage(null);
    }, [mappings]);

    const saveEdit = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        try {
            const missingRequired = applicableFields.filter(
                (f) => f.required && !editMappings[f.fieldType],
            );
            if (missingRequired.length > 0) {
                setError(
                    `Missing required fields: ${missingRequired.map((f) => f.label).join(", ")}`,
                );
                setIsSaving(false);
                return;
            }

            const mappingsList = applicableFields
                .filter((f) => editMappings[f.fieldType])
                .map((f) => ({
                    field_type: f.fieldType,
                    column_name: editMappings[f.fieldType],
                }));

            await actions.updateMappings(
                dealId,
                fileType,
                currentHeaderRow,
                mappingsList,
                storedAvailableColumns ?? undefined,
            );
            await actions.fetchMappings(dealId, fileType);
            setMode("view");
            setSuccessMessage("Column mappings updated successfully");
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save mappings");
        } finally {
            setIsSaving(false);
        }
    }, [
        dealId,
        fileType,
        currentHeaderRow,
        editMappings,
        applicableFields,
        actions,
        storedAvailableColumns,
    ]);

    const startUploadFlow = useCallback(() => {
        setMode("upload");
        setUploadFile(null);
        setExtractedColumns([]);
        setUploadMappings({} as Record<TApiColumnMappingFieldType, string>);
        setUploadHeaderRow(currentHeaderRow);
        setExtractError(null);
        setError(null);
        setSuccessMessage(null);
    }, [currentHeaderRow]);

    const handleFileSelect = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setUploadFile(file);
            setExtractError(null);
            setIsExtracting(true);
            try {
                const result = await actions.extractColumns(file, uploadHeaderRow);
                if (result.success) {
                    setExtractedColumns(result.columns);
                    const existing: Record<TApiColumnMappingFieldType, string> = {} as Record<
                        TApiColumnMappingFieldType,
                        string
                    >;
                    for (const m of mappings ?? []) {
                        const formula = parseFormulaExpression(m.columnName);
                        if (formula) {
                            if (
                                result.columns.includes(formula.left) &&
                                result.columns.includes(formula.right)
                            ) {
                                existing[m.fieldType] = m.columnName;
                            }
                        } else if (result.columns.includes(m.columnName)) {
                            existing[m.fieldType] = m.columnName;
                        }
                    }
                    setUploadMappings(existing);
                } else {
                    setExtractError(result.errors.join(", "));
                }
            } catch (err) {
                setExtractError(err instanceof Error ? err.message : "Failed to extract columns");
            } finally {
                setIsExtracting(false);
            }
        },
        [uploadHeaderRow, actions, mappings],
    );

    const handleHeaderRowChange = useCallback(
        async (newRow: number) => {
            setUploadHeaderRow(newRow);
            if (uploadFile) {
                setIsExtracting(true);
                setExtractError(null);
                try {
                    const result = await actions.extractColumns(uploadFile, newRow);
                    if (result.success) {
                        setExtractedColumns(result.columns);
                        setUploadMappings({} as Record<TApiColumnMappingFieldType, string>);
                    } else {
                        setExtractError(result.errors.join(", "));
                    }
                } catch (err) {
                    setExtractError(
                        err instanceof Error ? err.message : "Failed to extract columns",
                    );
                } finally {
                    setIsExtracting(false);
                }
            }
        },
        [uploadFile, actions],
    );

    const saveUploadMappings = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        try {
            const missingRequired = applicableFields.filter(
                (f) => f.required && !uploadMappings[f.fieldType],
            );
            if (missingRequired.length > 0) {
                setError(
                    `Missing required fields: ${missingRequired.map((f) => f.label).join(", ")}`,
                );
                setIsSaving(false);
                return;
            }

            const mappingsList = applicableFields
                .filter((f) => uploadMappings[f.fieldType])
                .map((f) => ({
                    field_type: f.fieldType,
                    column_name: uploadMappings[f.fieldType],
                }));

            await actions.updateMappings(
                dealId,
                fileType,
                uploadHeaderRow,
                mappingsList,
                extractedColumns,
            );
            await actions.fetchMappings(dealId, fileType);
            setMode("view");
            setUploadFile(null);
            setExtractedColumns([]);
            setUploadMappings({} as Record<TApiColumnMappingFieldType, string>);
            setSuccessMessage("Column mappings updated successfully");
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save mappings");
        } finally {
            setIsSaving(false);
        }
    }, [
        dealId,
        fileType,
        uploadHeaderRow,
        uploadMappings,
        extractedColumns,
        applicableFields,
        actions,
    ]);

    const cancelMode = useCallback(() => {
        setMode("view");
        setUploadFile(null);
        setExtractedColumns([]);
        setUploadMappings({} as Record<TApiColumnMappingFieldType, string>);
        setExtractError(null);
        setError(null);
    }, []);

    if (loading) {
        return (
            <Card className="p-6">
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                    <span className="ml-2 text-muted-foreground">Loading column mappings...</span>
                </div>
            </Card>
        );
    }

    const fileTypeLabel = fileType === "tb" ? "Trial Balance" : "General Ledger";

    // Shared dropdown renderer — amount field gets a "Use formula" button beside it
    const renderMappingDropdowns = (
        columns: string[],
        currentMappings: Record<TApiColumnMappingFieldType, string>,
        onChangeFn: (fieldType: TApiColumnMappingFieldType, value: string) => void,
        onOpenFormula: () => void,
    ) => (
        <div className="space-y-3">
            {applicableFields.map((field) => {
                const isAmount = field.fieldType === "amount";
                const currentValue = currentMappings[field.fieldType] ?? "";
                const isFormulaActive = isAmount && !!parseFormulaExpression(currentValue);

                return (
                    <div key={field.fieldType} className="flex items-center gap-3">
                        <label className="w-56 text-sm font-medium flex items-center gap-1">
                            {field.label}
                            {field.required && <span className="text-destructive">*</span>}
                        </label>

                        {isAmount ? (
                            <div className="flex-1 flex items-center gap-2">
                                {isFormulaActive ? (
                                    // Show formula as a read-only badge, button to edit
                                    <div
                                        className={cn(
                                            "flex-1 px-2 py-1.5 text-sm border rounded-md bg-muted text-muted-foreground font-mono",
                                        )}
                                    >
                                        {formatColumnDisplay(currentValue)}
                                    </div>
                                ) : (
                                    <select
                                        value={currentValue}
                                        onChange={(e) =>
                                            onChangeFn(field.fieldType, e.target.value)
                                        }
                                        className={cn(
                                            "flex-1 px-2 py-1.5 text-sm border rounded-md bg-background",
                                            field.required && !currentValue && "border-destructive",
                                        )}
                                    >
                                        <option value="">-- Select column --</option>
                                        {columns.map((col) => (
                                            <option key={col} value={col}>
                                                {col}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <div className="flex gap-1 shrink-0">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={onOpenFormula}
                                        className="whitespace-nowrap text-xs"
                                    >
                                        {isFormulaActive
                                            ? "Edit formula"
                                            : "Use formula for Amount Header"}
                                    </Button>
                                    {isFormulaActive && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => onChangeFn("amount", "")}
                                            className="text-xs px-2"
                                            title="Clear formula"
                                        >
                                            <X className="h-3 w-3" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <select
                                value={currentValue}
                                onChange={(e) => onChangeFn(field.fieldType, e.target.value)}
                                className={cn(
                                    "flex-1 px-2 py-1.5 text-sm border rounded-md bg-background",
                                    field.required && !currentValue && "border-destructive",
                                )}
                            >
                                <option value="">-- Select column --</option>
                                {columns.map((col) => (
                                    <option key={col} value={col}>
                                        {col}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                );
            })}
        </div>
    );

    // Derive formula state from current mappings for modal initialization
    const editFormula = parseFormulaExpression(editMappings["amount"] ?? "");
    const uploadFormula = parseFormulaExpression(uploadMappings["amount"] ?? "");

    return (
        <div className="space-y-4">
            {/* Success message */}
            {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
                    {successMessage}
                </div>
            )}

            {/* Error message */}
            {error && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {error}
                </div>
            )}

            {/* VIEW MODE */}
            {mode === "view" && (
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex gap-2">
                            {canEdit &&
                                mappings &&
                                mappings.length > 0 &&
                                storedAvailableColumns && (
                                    <Button variant="outline" size="sm" onClick={startEdit}>
                                        <Edit2 className="h-4 w-4 mr-2" />
                                        Edit
                                    </Button>
                                )}
                            {canEdit && (
                                <Button variant="outline" size="sm" onClick={startUploadFlow}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Amend column mapping
                                </Button>
                            )}
                        </div>
                    </div>

                    {!mappings || mappings.length === 0 ? (
                        <EmptyState
                            title="No column mappings configured"
                            description={
                                canEdit
                                    ? "Upload a sample Excel file to configure column mappings."
                                    : "No column mappings have been configured for this deal."
                            }
                            action={
                                canEdit
                                    ? { label: "Upload File", onClick: startUploadFlow }
                                    : undefined
                            }
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                                            Field
                                        </th>
                                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                                            Mapped Column
                                        </th>
                                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                                            Header Row
                                        </th>
                                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                                            Required
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {applicableFields.map((field) => {
                                        const mapping = mappings.find(
                                            (m) => m.fieldType === field.fieldType,
                                        );
                                        return (
                                            <tr
                                                key={field.fieldType}
                                                className="border-b last:border-b-0 even:bg-muted/40"
                                            >
                                                <td className="py-2 px-3 font-medium">
                                                    {field.label}
                                                </td>
                                                <td className="py-2 px-3">
                                                    {mapping ? (
                                                        <span>
                                                            {formatColumnDisplay(
                                                                mapping.columnName,
                                                            )}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted-foreground italic">
                                                            Not mapped
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-2 px-3 text-muted-foreground">
                                                    {mapping?.headerRowNumber ?? "-"}
                                                </td>
                                                <td className="py-2 px-3">
                                                    <Badge
                                                        variant={
                                                            field.required ? "default" : "secondary"
                                                        }
                                                    >
                                                        {field.required ? "Required" : "Optional"}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            )}

            {/* EDIT MODE (dropdown from stored available columns) */}
            {mode === "edit" && storedAvailableColumns && (
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-lg font-medium">
                                Edit {fileTypeLabel} Column Mappings
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                Change which columns map to each field using the columns from the
                                current file.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={cancelMode}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button size="sm" onClick={saveEdit} disabled={isSaving}>
                                <Save className="h-4 w-4 mr-2" />
                                {isSaving ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </div>

                    {renderMappingDropdowns(
                        storedAvailableColumns,
                        editMappings,
                        (fieldType, value) =>
                            setEditMappings((prev) => ({ ...prev, [fieldType]: value })),
                        () => setEditFormulaOpen(true),
                    )}

                    <FormulaModal
                        open={editFormulaOpen}
                        onClose={() => setEditFormulaOpen(false)}
                        columns={storedAvailableColumns}
                        initialLeft={editFormula?.left ?? ""}
                        initialOp={editFormula?.op ?? "-"}
                        initialRight={editFormula?.right ?? ""}
                        onSubmit={(left, op, right) =>
                            setEditMappings((prev) => ({
                                ...prev,
                                amount: buildFormulaExpression(left, op, right),
                            }))
                        }
                    />
                </Card>
            )}

            {/* UPLOAD MODE (new file) */}
            {mode === "upload" && (
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-lg font-medium">
                                Upload {fileTypeLabel} Sample File
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                Upload a sample Excel file to extract column headers and map fields.
                            </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={cancelMode}>
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                        </Button>
                    </div>

                    {/* Header Row */}
                    <div className="mb-4 flex items-center gap-3">
                        <label className="text-sm font-medium">Header Row Number:</label>
                        <input
                            type="number"
                            min={1}
                            value={uploadHeaderRow}
                            onChange={(e) =>
                                handleHeaderRowChange(Math.max(1, parseInt(e.target.value) || 1))
                            }
                            className="w-20 px-2 py-1 text-sm border rounded-md"
                        />
                    </div>

                    {/* File Upload */}
                    <div className="mb-6">
                        <label
                            htmlFor={`column-mapping-upload-${fileType}`}
                            className={cn(
                                "flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer",
                                "hover:bg-muted/50 transition-colors",
                                uploadFile ? "border-primary bg-primary/5" : "border-muted",
                            )}
                        >
                            {isExtracting ? (
                                <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                                    <span className="text-sm text-muted-foreground">
                                        Extracting columns...
                                    </span>
                                </div>
                            ) : uploadFile ? (
                                <div className="flex items-center gap-2">
                                    <FileSpreadsheet className="h-6 w-6 text-primary" />
                                    <span className="text-sm font-medium">{uploadFile.name}</span>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-1">
                                    <Upload className="h-8 w-8 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">
                                        Click to upload .xlsx file
                                    </span>
                                </div>
                            )}
                            <input
                                id={`column-mapping-upload-${fileType}`}
                                type="file"
                                accept=".xlsx"
                                className="hidden"
                                onChange={handleFileSelect}
                            />
                        </label>
                    </div>

                    {/* Extract Error */}
                    {extractError && (
                        <div className="mb-4 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm">
                            {extractError}
                        </div>
                    )}

                    {/* Column Mapping Dropdowns */}
                    {extractedColumns.length > 0 && (
                        <>
                            <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                Map fields to columns:
                            </h4>
                            {renderMappingDropdowns(
                                extractedColumns,
                                uploadMappings,
                                (fieldType, value) =>
                                    setUploadMappings((prev) => ({ ...prev, [fieldType]: value })),
                                () => setUploadFormulaOpen(true),
                            )}
                            <div className="flex justify-end pt-4">
                                <Button onClick={saveUploadMappings} disabled={isSaving}>
                                    <Save className="h-4 w-4 mr-2" />
                                    {isSaving ? "Saving..." : "Save Mappings"}
                                </Button>
                            </div>

                            <FormulaModal
                                open={uploadFormulaOpen}
                                onClose={() => setUploadFormulaOpen(false)}
                                columns={extractedColumns}
                                initialLeft={uploadFormula?.left ?? ""}
                                initialOp={uploadFormula?.op ?? "-"}
                                initialRight={uploadFormula?.right ?? ""}
                                onSubmit={(left, op, right) =>
                                    setUploadMappings((prev) => ({
                                        ...prev,
                                        amount: buildFormulaExpression(left, op, right),
                                    }))
                                }
                            />
                        </>
                    )}
                </Card>
            )}
        </div>
    );
};
